﻿import { CalendarDays, Loader2, Plus, Trash2, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { Trans } from "react-i18next";
import { toast } from "sonner";
import type { ShopEvent, ShopEventTicketInfo } from "@ticketnjoy/types";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useEventQuery, useEventsQuery } from "@/features/events/api";
import { formatEventDate } from "@/features/events/date-helpers";
import { useConfirm } from "@/lib/confirm";
import { useFormat } from "@/lib/format";
import { useT } from "@/lib/i18n";

import {
  useAttachEventMutation,
  useDetachEventMutation,
  useShopEventsQuery,
  useShopEventVisibilityMutation,
  useUpdateShopEventMetadataMutation,
  useUpdateShopEventTicketsMutation,
} from "./api";

/**
 * Shop detay sayfasında "Bu mağazadaki etkinlikler" kartı.
 * Etkinlik eklenirken organizer hangi ticket type'ların bu mağazada satılacağını zorunlu seçer.
 * Aynı event farklı mağazalarda farklı ticket set'leri (farklı currency'ler) ile satılabilir.
 */
export function ShopEventsSection({ shopSlug }: { shopSlug: string }) {
  const { data: links, isLoading } = useShopEventsQuery(shopSlug);
  const t = useT("shopEvents");
  const [showPicker, setShowPicker] = useState(false);

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold">{t("title")}</h2>
          <p className="text-xs text-muted-foreground">{t("description")}</p>
        </div>
        {!showPicker && (
          <Button size="sm" onClick={() => setShowPicker(true)} className="shrink-0">
            <Plus className="me-1 h-4 w-4" /> {t("add")}
          </Button>
        )}
      </div>

      {showPicker && (
        <EventPicker
          shopSlug={shopSlug}
          attachedEventIds={new Set((links ?? []).map((l) => l.eventId))}
          onDone={() => setShowPicker(false)}
        />
      )}

      {!links || links.length === 0 ? (
        <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
          {t("empty")}
        </div>
      ) : (
        <div className="space-y-2">
          {links.map((l) => (
            <ShopEventRow key={l.id} shopSlug={shopSlug} link={l} />
          ))}
        </div>
      )}
    </div>
  );
}

function ShopEventRow({ shopSlug, link }: { shopSlug: string; link: ShopEvent }) {
  const metadataMutation = useUpdateShopEventMetadataMutation(shopSlug, link.id);
  const visibilityMutation = useShopEventVisibilityMutation(shopSlug, link.id);
  const detachMutation = useDetachEventMutation(shopSlug);
  const confirm = useConfirm();
  const tConfirm = useT("confirm.detachEvent");
  const t = useT("shopEvents");
  const tCommon = useT("common");
  const tStatus = useT("eventStatus");
  const fmt = useFormat();
  const [editing, setEditing] = useState(false);
  const [editingTickets, setEditingTickets] = useState(false);
  const [section, setSection] = useState(link.section ?? "");
  const [order, setOrder] = useState(link.displayOrder);

  const toggleVisibility = () => {
    const action = link.status === "Listed" ? "hide" : "show";
    visibilityMutation.mutate(action, {
      onError: (err) => toast.error(err.message),
    });
  };

  const handleDetach = async () => {
    const ok = await confirm({
      title: tConfirm("title"),
      description: tConfirm("description", { title: link.eventTitle }),
      confirmLabel: tConfirm("action"),
      destructive: true,
    });
    if (!ok) return;
    detachMutation.mutate(link.id, {
      onSuccess: () => toast.success(t("removed")),
      onError: (err) => toast.error(err.message),
    });
  };

  const saveEdit = () => {
    metadataMutation.mutate(
      { section: section || null, displayOrder: order },
      {
        onSuccess: () => {
          toast.success(t("updated"));
          setEditing(false);
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  return (
    <div className="rounded-md border p-3">
      {/* Mobile-first: içerik üstte tam genişlik, aksiyonlar altta satır halinde.
          sm+ üzerinde klasik sola içerik / sağa aksiyonlar düzenine döner. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-2 sm:flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/events/${link.eventSlug}`}
              className="break-words font-medium hover:underline"
            >
              {link.eventTitle}
            </Link>
            <Badge variant="outline">{tStatus(link.eventStatus)}</Badge>
            <Badge variant={link.status === "Listed" ? "success" : "secondary"}>
              {t(`linkStatus.${link.status}`)}
            </Badge>
            {link.section && <Badge variant="outline">{link.section}</Badge>}
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <CalendarDays className="h-3 w-3" />
              {formatEventDate(link.eventStartsAt, fmt)}
            </span>
            {link.eventVenueCity && <span>· {link.eventVenueCity}</span>}
            <span>· {t("order", { value: link.displayOrder })}</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {link.attachedTickets.length === 0 ? (
              <span className="text-xs text-destructive">{t("noTicketsAttached")}</span>
            ) : (
              link.attachedTickets.map((ticket) => (
                <Badge key={ticket.ticketTypeId} variant="secondary" className="text-xs">
                  {ticket.name} · {fmt.money(ticket.price, ticket.currency)}
                </Badge>
              ))
            )}
          </div>
        </div>
        {/* Aksiyonlar: mobilde metin butonları eşit paylaşır, icon-only trash
            uçta kendi genişliğinde kalır. sm+ üzerinde natural width. */}
        <div className="flex flex-wrap items-center gap-1 sm:shrink-0 sm:justify-end">
          <Button
            size="sm"
            variant="ghost"
            onClick={toggleVisibility}
            disabled={visibilityMutation.isPending}
            className="flex-1 sm:flex-none"
          >
            {link.status === "Listed" ? t("hide") : t("show")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setEditing((v) => !v)}
            className="flex-1 sm:flex-none"
          >
            {editing ? tCommon("cancel") : tCommon("edit")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setEditingTickets((v) => !v)}
            className="flex-1 sm:flex-none"
          >
            {t("editTicketsButton")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDetach}
            disabled={detachMutation.isPending}
            aria-label={t("remove")}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {editing && (
        <div className="mt-3 grid gap-3 sm:grid-cols-[2fr_1fr_auto]">
          <div>
            <label className="text-xs text-muted-foreground">{t("sectionLabel")}</label>
            <Input
              placeholder={t("sectionPlaceholder")}
              value={section}
              onChange={(e) => setSection(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">{t("sortOrder")}</label>
            <Input
              type="number"
              value={order}
              onChange={(e) => setOrder(Number(e.target.value))}
            />
          </div>
          <div className="flex items-end">
            <Button size="sm" onClick={saveEdit} disabled={metadataMutation.isPending}>
              {tCommon("save")}
            </Button>
          </div>
        </div>
      )}

      {editingTickets && (
        <TicketSelectionEditor
          shopSlug={shopSlug}
          shopEventId={link.id}
          eventSlug={link.eventSlug}
          currentTickets={link.attachedTickets}
          onDone={() => setEditingTickets(false)}
        />
      )}
    </div>
  );
}

/**
 * Mevcut ShopEvent altındaki ticket type listesini düzenler.
 * Event'in tüm ticket type'larını gösterir, halihazırda bu mağazada satılanlar işaretli gelir.
 */
function TicketSelectionEditor({
  shopSlug,
  shopEventId,
  eventSlug,
  currentTickets,
  onDone,
}: {
  shopSlug: string;
  shopEventId: string;
  eventSlug: string;
  currentTickets: ShopEventTicketInfo[];
  onDone: () => void;
}) {
  const { data: event, isLoading } = useEventQuery(eventSlug);
  const mutation = useUpdateShopEventTicketsMutation(shopSlug, shopEventId);
  const t = useT("shopEvents");
  const tCommon = useT("common");
  const fmt = useFormat();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(currentTickets.map((ticket) => ticket.ticketTypeId)),
  );

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedCurrencies = useMemo(() => {
    if (!event) return [] as string[];
    const currencies = event.ticketTypes
      .filter((ticket) => selectedIds.has(ticket.id))
      .map((ticket) => ticket.currency);
    return Array.from(new Set(currencies));
  }, [event, selectedIds]);

  const isMixed = selectedCurrencies.length > 1;

  const save = () => {
    if (selectedIds.size === 0) {
      toast.error(t("atLeastOneRequired"));
      return;
    }
    mutation.mutate(
      { ticketTypeIds: Array.from(selectedIds) },
      {
        onSuccess: () => {
          toast.success(t("ticketsUpdated"));
          onDone();
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  return (
    <div className="mt-3 rounded-md border border-dashed p-3">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-medium">{t("ticketsForShop")}</h4>
        <Button variant="ghost" size="sm" onClick={onDone} aria-label={tCommon("close")}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {isLoading || !event ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : event.ticketTypes.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("noTicketsShort")}</p>
      ) : (
        <>
          <div className="space-y-1.5">
            {event.ticketTypes.map((ticket) => (
              <label
                key={ticket.id}
                className="flex cursor-pointer items-center gap-2 rounded border p-2 text-sm transition hover:bg-accent"
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(ticket.id)}
                  onChange={() => toggle(ticket.id)}
                />
                <span className="flex-1 font-medium">{ticket.name}</span>
                <Badge variant="secondary" className="font-mono text-xs">
                  {ticket.currency}
                </Badge>
                <span className="text-muted-foreground">
                  {fmt.money(ticket.price, ticket.currency)}
                </span>
              </label>
            ))}
          </div>
          {isMixed && (
            <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
              ⚠ {t("mixedCurrencyWarning", { currencies: selectedCurrencies.join(", ") })}
            </p>
          )}
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={onDone}>
              {tCommon("cancel")}
            </Button>
            <Button size="sm" onClick={save} disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              {tCommon("save")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function EventPicker({
  shopSlug,
  attachedEventIds,
  onDone,
}: {
  shopSlug: string;
  attachedEventIds: Set<string>;
  onDone: () => void;
}) {
  const { data: events, isLoading } = useEventsQuery();
  const t = useT("shopEvents");
  const tCommon = useT("common");
  const tEvents = useT("events");
  const tStatus = useT("eventStatus");
  const fmt = useFormat();
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [section, setSection] = useState("");

  const available = (events ?? []).filter((e) => !attachedEventIds.has(e.id));
  const selectedEvent = available.find((e) => e.id === selectedEventId);

  return (
    <div className="rounded-md border-2 border-dashed p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold">
          {selectedEventId ? t("pickTicketsTitle") : t("pickEventTitle")}
        </h3>
        <Button variant="ghost" size="sm" onClick={onDone} aria-label={tCommon("close")}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {!selectedEventId && (
        <>
          <div className="mb-3">
            <label className="text-xs text-muted-foreground">{t("sectionOptional")}</label>
            <Input
              placeholder={t("sectionPlaceholder")}
              value={section}
              onChange={(e) => setSection(e.target.value)}
            />
          </div>

          {isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : available.length === 0 ? (
            <div className="space-y-3 py-2 text-center">
              <p className="text-sm text-muted-foreground">{t("noAvailable")}</p>
              <Button size="sm" variant="outline" asChild>
                <Link to="/events/new">{tEvents("newEvent")}</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {available.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => setSelectedEventId(e.id)}
                  className="flex w-full items-center justify-between rounded border p-2 text-start transition hover:bg-accent"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{e.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatEventDate(e.startsAt, fmt)} · {tStatus(e.status)}
                    </p>
                  </div>
                  <Badge variant="outline">{t("select")}</Badge>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {selectedEventId && selectedEvent && (
        <TicketPickerAndAttach
          shopSlug={shopSlug}
          eventSlug={selectedEvent.slug}
          eventTitle={selectedEvent.title}
          section={section}
          onBack={() => setSelectedEventId(null)}
          onSuccess={onDone}
        />
      )}
    </div>
  );
}

/**
 * Event seçildikten sonra gösterilen ticket type multi-select + attach butonu.
 * Ticket'ları event detail query ile yükleriz (currency dahil).
 */
function TicketPickerAndAttach({
  shopSlug,
  eventSlug,
  eventTitle,
  section,
  onBack,
  onSuccess,
}: {
  shopSlug: string;
  eventSlug: string;
  eventTitle: string;
  section: string;
  onBack: () => void;
  onSuccess: () => void;
}) {
  const { data: event, isLoading } = useEventQuery(eventSlug);
  const attachMutation = useAttachEventMutation(shopSlug);
  const t = useT("shopEvents");
  const fmt = useFormat();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedCurrencies = useMemo(() => {
    if (!event) return [] as string[];
    const currencies = event.ticketTypes
      .filter((ticket) => selectedIds.has(ticket.id))
      .map((ticket) => ticket.currency);
    return Array.from(new Set(currencies));
  }, [event, selectedIds]);

  const isMixed = selectedCurrencies.length > 1;

  const handleAttach = () => {
    if (selectedIds.size === 0) {
      toast.error(t("atLeastOneRequired"));
      return;
    }
    attachMutation.mutate(
      {
        eventId: event!.id,
        ticketTypeIds: Array.from(selectedIds),
        section: section || null,
        displayOrder: 0,
      },
      {
        onSuccess: () => {
          toast.success(t("attached", { title: eventTitle }));
          onSuccess();
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  if (isLoading || !event) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (event.ticketTypes.length === 0) {
    return (
      <div className="space-y-3 py-2 text-center">
        <p className="text-sm text-muted-foreground">{t("noEventTickets")}</p>
        <div className="flex justify-center gap-2">
          <Button size="sm" variant="outline" onClick={onBack}>
            {t("back")}
          </Button>
          <Button size="sm" variant="outline" asChild>
            <Link to={`/events/${eventSlug}`}>{t("openEvent")}</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <p className="mb-2 text-xs text-muted-foreground">
        <Trans
          i18nKey="shopEvents.pickTicketsFor"
          values={{ title: eventTitle }}
          components={{ b: <span className="font-medium" /> }}
        />
      </p>
      <div className="space-y-1.5">
        {event.ticketTypes.map((ticket) => (
          <label
            key={ticket.id}
            className="flex cursor-pointer items-center gap-2 rounded border p-2 text-sm transition hover:bg-accent"
          >
            <input
              type="checkbox"
              checked={selectedIds.has(ticket.id)}
              onChange={() => toggle(ticket.id)}
            />
            <span className="flex-1 font-medium">{ticket.name}</span>
            <Badge variant="secondary" className="font-mono text-xs">
              {ticket.currency}
            </Badge>
            <span className="text-muted-foreground">
              {fmt.money(ticket.price, ticket.currency)}
            </span>
          </label>
        ))}
      </div>

      {isMixed && (
        <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
          ⚠ {t("mixedCurrencyWarning", { currencies: selectedCurrencies.join(", ") })}
        </p>
      )}

      <div className="mt-3 flex justify-between gap-2">
        <Button size="sm" variant="ghost" onClick={onBack}>
          {t("back")}
        </Button>
        <Button size="sm" onClick={handleAttach} disabled={attachMutation.isPending}>
          {attachMutation.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
          {t("attachToShop")}
        </Button>
      </div>
    </>
  );
}
